import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";

import HttpManager from "../../../../services/HttpManager";
import { Loading, apiError } from "../kits/shared";
import { IconoAmpliar, IconoLapiz, VentanaTienda } from "./piezasTienda";
import { UnificacionesHistorial } from "./unificarProductos";

/*
	Ficha del producto (Esteban, 2026-11-03): se abre al tocar el nombre en Bodegas y en Productos. Ventana con X (como los
	módulos nuevos), foto que se amplía a pantalla completa con zoom, el nombre se cambia con el lápiz (permiso de
	Productos) y la existencia en una tabla limpia: bodegas en columnas, tallas/colores en renglones, ceros como guion.
	Unificar repetidos se hace desde la lista (cuadritos); aquí queda su historial con Deshacer (Super Admin).
*/

interface Props {
	campusId: number;
	productId: number;
	currency: string;
	/** Bit Crear de la sección Productos: el back pide ese bit para guardar (POST _update). */
	canRename: boolean;
	onClose: () => void;
	/** Para que la lista de atrás se refresque (nombre nuevo, deshacer una unificación). */
	onRenamed?: (name: string) => void;
}

const zero = <span className="wti-cero">–</span>;

const FichaProducto: React.FC<Props> = ({ campusId, productId, currency, canRename, onClose, onRenamed }) => {
	const http = HttpManager.getInstance().inventoryService;
	const [product, setProduct] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [photoUrl, setPhotoUrl] = useState<string | null>(null);
	const [photoFailed, setPhotoFailed] = useState<boolean>(false);
	const [zoomOpen, setZoomOpen] = useState<boolean>(false);
	const [editing, setEditing] = useState<boolean>(false);
	const [draft, setDraft] = useState<string>("");
	const [saving, setSaving] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	/** Si ya había otro producto con ese nombre, el back lo guarda como "Nombre (2)": se avisa cuál quedó. */
	const [notice, setNotice] = useState<string | null>(null);
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const isSuperAdmin = currentUser?.currentRole === "superAdmin";
	const [reloadKey, setReloadKey] = useState<number>(0);

	useEffect(() => {
		let alive = true;
		setError(null);
		http.get(campusId, productId)
			.then((response) => { if (alive) setProduct(response.data); })
			.catch((requestError) => { if (alive) setError(apiError(requestError, "No se pudo abrir el producto.")); });
		return () => { alive = false; };
	}, [campusId, productId, reloadKey]);

	// La foto original (no la miniatura de 160 px): aquí se ve en grande y se amplía.
	useEffect(() => {
		setPhotoUrl(null);
		if (!product?.hasPhoto) return;
		let objectUrl: string | null = null;
		let alive = true;
		setPhotoFailed(false);
		http.photoBlob(campusId, product.id, false)
			.then((response) => { if (!alive) return; objectUrl = URL.createObjectURL(response.data); setPhotoUrl(objectUrl); })
			.catch(() => { if (alive) setPhotoFailed(true); });
		return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
	}, [campusId, product?.id, product?.hasPhoto]);

	/** X o Escape: primero cierra lo de más arriba (foto ampliada, edición del nombre, el campo donde se escribe). */
	const onHide = () => {
		const focused = document.activeElement as HTMLElement | null;
		if (zoomOpen) setZoomOpen(false);
		else if (editing) { if (!saving) setEditing(false); }
		else if (focused && ["INPUT", "TEXTAREA", "SELECT"].includes(focused.tagName) && focused.closest(".wtiFicha")) focused.blur();
		else onClose();
	};

	const startEdit = () => { setDraft(product.name); setSaveError(null); setNotice(null); setEditing(true); };

	const saveName = async () => {
		const name = draft.trim().replace(/\s+/g, " ");
		if (name === "" || name === product.name) { setEditing(false); return; }
		setSaving(true);
		setSaveError(null);
		try {
			const response = await http.update(campusId, product.id, { name }, null);
			const saved = String(response.data?.product?.name || name);
			setProduct(response.data?.product ? { ...product, ...response.data.product } : { ...product, name: saved });
			setNotice(saved !== name ? "Ya había otro producto llamado «" + name + "»; este quedó como «" + saved + "»." : null);
			setEditing(false);
			if (onRenamed) onRenamed(saved);
		} catch (requestError) {
			setSaveError(apiError(requestError, "No se pudo cambiar el nombre."));
		} finally {
			setSaving(false);
		}
	};

	const total = product ? totalOnHand(product) : 0;
	const counted = product && (product.trackStock || (product.variants || []).length > 0);

	const titulo = !product ? "Producto"
		: editing ? <span style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", width: "100%" }}>
			<input className="wau-entrada" style={{ flex: "1 1 240px", fontSize: 16 }} autoFocus maxLength={255} value={draft} disabled={saving} aria-label="Nombre del producto"
				onFocus={(event) => event.target.select()} onChange={(event) => setDraft(event.target.value)}
				onKeyDown={(event) => { if (event.key === "Enter") saveName(); }} />
			<button type="button" className={"wau-btn p mini" + (draft.trim() === "" ? " wti-apagado" : "")} disabled={saving} title={draft.trim() === "" ? "Escribe el nombre." : undefined}
				onClick={() => { if (draft.trim() === "") { setSaveError("Escribe el nombre del producto."); return; } saveName(); }}>{saving ? "Guardando…" : "Guardar"}</button>
			<button type="button" className="wau-btn s mini" disabled={saving} onClick={() => setEditing(false)}>Cancelar</button>
		</span>
		: <>
			<span>{product.name}</span>
			{canRename && !product.modelId ? <button type="button" className="wti-icono" title="Cambiar nombre" aria-label="Cambiar nombre" onClick={startEdit}><IconoLapiz /></button> : null}
			{product.soldOutAt && !product.deprecatedAt ? <span className="waAgotado">Agotado</span> : null}
		</>;

	return <>
		<VentanaTienda className="wtiFicha" titulo={titulo} onHide={onHide} busy={saving} xl enforceFocus={!zoomOpen}>
			{error ? <div className="wau-aviso">{error}</div>
			: !product ? <Loading />
			: <>
				{saveError ? <div className="wau-aviso" style={{ color: "var(--wa-oro)" }}>{saveError}</div> : null}
				{notice ? <div className="wau-aviso ok"><span>{notice}</span><button type="button" aria-label="Cerrar aviso" onClick={() => setNotice(null)}>×</button></div> : null}
				<div className="wti-ficha-top">
					<button type="button" className={"wti-foto" + (photoUrl ? "" : " vacia")} disabled={!photoUrl} onClick={() => setZoomOpen(true)} aria-label={photoUrl ? "Ampliar foto" : undefined}>
						{photoUrl ? <><img src={photoUrl} alt={product.name} /><span className="lupa"><IconoAmpliar /></span></>
							: <span>{!product.hasPhoto ? "Sin foto" : photoFailed ? "No se pudo cargar la foto" : "Cargando foto…"}</span>}
					</button>
					<div style={{ minWidth: 0 }}>
						<div className="wti-cifras">
							<div className="wti-cifra"><small>Precio</small>{product.pricePending
								? <b className="wau-oro" style={{ fontSize: 16 }}>Sin precio</b>
								: <b>{currency}{Number(product.basePrice ?? product.price ?? 0).toFixed(2)}</b>}
								{product.pricePending && product.suggestedPrice ? <div className="wti-sub">sugerido {currency}{Number(product.suggestedPrice).toFixed(2)}</div> : null}</div>
							{counted ? <div className="wti-cifra"><small>Existencia</small><b>{total}</b> <span className="wti-sub">{product.unit || "pieza"}{total === 1 ? "" : "s"}</span></div> : null}
						</div>
						<dl className="wau-kv">
							<dt>Tipo</dt><dd>{product.kindLabel}</dd>
							<dt>Código WA</dt><dd>{product.waCode || "—"}</dd>
							{product.barcode ? <><dt>Código de barras</dt><dd>{product.barcode}</dd></> : null}
							{product.deprecatedAt ? <><dt>Estado</dt><dd className="wau-oro">Dado de baja el {product.deprecatedAt}</dd></> : null}
						</dl>
						{product.description ? <p style={{ fontSize: 14, marginTop: 12, marginBottom: 0, whiteSpace: "pre-wrap" }}>{product.description}</p> : null}
					</div>
				</div>

				<Existencia product={product} currency={currency} />

				{(product.version?.components || []).length ? <div className="wti-sec">
					<h3>Piezas · {product.version.label}</h3>
					<ul className="wti-lista-simple">{product.version.components.map((component: any) => <li key={component.id}>
						{component.name} <b>× {component.quantity}</b>
						{component.substitutes.length ? <span className="wti-sub"> · o {component.substitutes.map((s: any) => s.name).join(", ")}</span> : null}
						{component.variantSummary ? <span className="wti-sub"> · {component.variantSummary.count} variantes</span>
							: component.sizeAtDelivery ? <span className="wti-sub"> · talla al entregar</span> : null}
					</li>)}</ul>
				</div> : null}

				{(product.courses || []).length ? <div className="wti-sec">
					<h3>Cursos</h3>
					<div style={{ fontSize: 14 }}>{product.courses.map((c: any) => c.course).join(" · ")}</div>
				</div> : null}

				{(product.versions || []).length > 1 ? <div className="wti-sec">
					<h3>Versiones de las piezas</h3>
					<ul className="wti-lista-simple">{product.versions.map((version: any) => <li key={version.id}>
						<b>{version.label}</b>{version.current ? <span className="wti-sub"> · vigente</span> : null}
						<span className="wti-sub"> · desde {version.effectiveFrom || "-"}{version.effectiveTo ? " hasta " + version.effectiveTo : ""}</span>
					</li>)}</ul>
				</div> : null}

				{isSuperAdmin ? <UnificacionesHistorial campusId={campusId} productId={product.id} refreshKey={reloadKey}
					onUndone={(message) => { setNotice(message); setReloadKey((key) => key + 1); if (onRenamed) onRenamed(product.name); }} /> : null}
			</>}
		</VentanaTienda>
		{zoomOpen && photoUrl ? ReactDOM.createPortal(<ZoomFoto url={photoUrl} alt={product?.name || ""} onClose={() => setZoomOpen(false)} />, document.body) : null}
	</>;
};

const totalOnHand = (product: any): number => (product.variants || []).length
	? product.variants.reduce((sum: number, variant: any) => sum + Number(variant.onHand || 0), 0)
	: (product.stockByWarehouse || []).reduce((sum: number, row: any) => sum + Number(row.onHand || 0), 0);

/** Nombre corto de la bodega para el encabezado: "Bodega Tienda SPS" → "Tienda SPS". */
const shortName = (name: string): string => String(name || "").replace(/^Bodega\s+/i, "");

/**
 * Existencia limpia: una tabla con las bodegas del país en columnas (las que tienen algo primero) y, si hay tallas o
 * colores, un renglón por cada una con su total; los ceros van como guion para que resalte lo que sí hay.
 */
const Existencia: React.FC<{ product: any; currency: string }> = ({ product, currency }) => {
	const variants: any[] = product.variants || [];
	if ((product.availability || []).length) {
		return <div className="wti-sec">
			<h3>Se pueden armar hoy</h3>
			<div className="wau-tabla"><table>
				<thead><tr><th>Bodega</th><th className="der">Kits</th><th>Limita</th></tr></thead>
				<tbody>{product.availability.map((row: any) => <tr key={row.warehouseId}>
					<td>{shortName(row.warehouse)}{row.delivers === false ? <span className="wti-sub"> · no entrega</span> : null}</td>
					<td className="der wau-num" style={{ fontWeight: 700 }}>{row.available > 0 ? row.available : zero}</td>
					<td className="wti-sub">{row.limitedBy || ""}</td>
				</tr>)}</tbody>
			</table></div>
		</div>;
	}
	if (!variants.length && !product.trackStock) return null;

	const rows: { key: number; label: React.ReactNode; levels: any[]; total: number; price?: string }[] = variants.length
		? variants.map((variant) => ({ key: variant.id, label: variant.label || variant.sizeValue || variant.name, levels: variant.stockByWarehouse || [], total: Number(variant.onHand || 0),
			price: variant.ownPrice ? currency + Number(variant.price).toFixed(2) : undefined }))
		: [{ key: product.id, label: "Piezas", levels: product.stockByWarehouse || [], total: totalOnHand(product) }];
	// Columnas: todas las bodegas del país; las que tienen piezas van primero, en su orden.
	const base: any[] = (rows[0]?.levels || []).map((level: any) => ({ id: level.warehouseId, code: level.code, name: level.name }));
	const columnTotal = (id: number) => rows.reduce((sum, row) => sum + Number((row.levels.find((level: any) => level.warehouseId === id) || {}).onHand || 0), 0);
	const columns = [...base.filter((w) => columnTotal(w.id) !== 0), ...base.filter((w) => columnTotal(w.id) === 0)];
	const cell = (row: typeof rows[number], id: number) => Number((row.levels.find((level: any) => level.warehouseId === id) || {}).onHand || 0);
	const grand = rows.reduce((sum, row) => sum + row.total, 0);

	return <div className="wti-sec">
		<h3>Existencia por bodega</h3>
		<div className="wau-tabla"><table>
			<thead><tr>
				<th>{variants.length ? (variants.some((v) => v.colorValue) ? "Talla / Color" : "Talla") : ""}</th>
				{columns.map((w) => <th key={w.id} className="der" title={w.name}>{shortName(w.name)}</th>)}
				<th className="der">Total</th>
			</tr></thead>
			<tbody>{rows.map((row) => <tr key={row.key}>
				<td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{row.label}{row.price ? <span className="wti-sub" style={{ fontWeight: 400 }}> · {row.price}</span> : null}</td>
				{columns.map((w) => { const value = cell(row, w.id); return <td key={w.id} className="der wau-num" style={{ fontWeight: value ? 600 : 400 }}>{value ? value : zero}</td>; })}
				<td className="der wau-num" style={{ fontWeight: 700 }}>{row.total ? row.total : zero}</td>
			</tr>)}</tbody>
			{rows.length > 1 ? <tfoot><tr>
				<td style={{ fontWeight: 700, borderBottom: 0 }}>Total</td>
				{columns.map((w) => { const value = columnTotal(w.id); return <td key={w.id} className="der wau-num" style={{ fontWeight: 700, borderBottom: 0 }}>{value ? value : zero}</td>; })}
				<td className="der wau-num" style={{ fontWeight: 800, borderBottom: 0 }}>{grand}</td>
			</tr></tfoot> : null}
		</table></div>
	</div>;
};

const MIN = 1;
const MAX = 6;

/** Foto a pantalla completa: rueda o pellizco para acercar, arrastrar para mover, doble clic para alternar. */
const ZoomFoto: React.FC<{ url: string; alt: string; onClose: () => void }> = ({ url, alt, onClose }) => {
	// Escala y posición juntas: el zoom mueve las dos a la vez y así nunca se desfasan.
	const [view, setView] = useState<{ s: number; x: number; y: number }>({ s: 1, x: 0, y: 0 });
	const scale = view.s;
	const stage = useRef<HTMLDivElement>(null);
	const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
	const gesture = useRef<{ dist: number; scale: number; last: { x: number; y: number } | null; moved: boolean }>({ dist: 0, scale: 1, last: null, moved: false });

	/** Acerca a la escala `next` dejando quieto el punto (px, py), medido desde el centro de la pantalla. */
	const zoomTo = useCallback((next: number | ((current: number) => number), px = 0, py = 0) => {
		setView((v) => {
			const target = Math.min(MAX, Math.max(MIN, typeof next === "function" ? next(v.s) : next));
			if (target === 1) return { s: 1, x: 0, y: 0 };
			return { s: target, x: px - (px - v.x) * (target / v.s), y: py - (py - v.y) * (target / v.s) };
		});
	}, []);
	const center = (clientX: number, clientY: number) => {
		const box = stage.current?.getBoundingClientRect();
		return box ? { x: clientX - box.left - box.width / 2, y: clientY - box.top - box.height / 2 } : { x: 0, y: 0 };
	};

	// La rueda necesita un listener no pasivo para que no se mueva la página de atrás.
	useEffect(() => {
		const el = stage.current;
		if (!el) return;
		const onWheel = (event: WheelEvent) => {
			event.preventDefault();
			const p = center(event.clientX, event.clientY);
			zoomTo((current) => current * Math.exp(-event.deltaY * 0.0025), p.x, p.y);
		};
		el.addEventListener("wheel", onWheel, { passive: false });
		return () => el.removeEventListener("wheel", onWheel);
	}, [zoomTo]);

	const onPointerDown = (event: React.PointerEvent) => {
		pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
		const list = Array.from(pointers.current.values());
		gesture.current.moved = false;
		if (list.length === 2) {
			gesture.current.dist = Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y);
			gesture.current.scale = view.s;
		}
		gesture.current.last = { x: event.clientX, y: event.clientY };
	};
	const onPointerMove = (event: React.PointerEvent) => {
		if (!pointers.current.has(event.pointerId)) return;
		pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
		const list = Array.from(pointers.current.values());
		if (list.length === 2 && gesture.current.dist > 0) {
			gesture.current.moved = true;
			const mid = center((list[0].x + list[1].x) / 2, (list[0].y + list[1].y) / 2);
			zoomTo(gesture.current.scale * Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) / gesture.current.dist, mid.x, mid.y);
			return;
		}
		const last = gesture.current.last;
		if (last && view.s > 1) {
			const dx = event.clientX - last.x;
			const dy = event.clientY - last.y;
			if (Math.abs(dx) + Math.abs(dy) > 2) gesture.current.moved = true;
			setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
		}
		gesture.current.last = { x: event.clientX, y: event.clientY };
	};
	const onPointerUp = (event: React.PointerEvent) => {
		pointers.current.delete(event.pointerId);
		const rest = Array.from(pointers.current.values());
		gesture.current.last = rest.length ? rest[0] : null;
		if (rest.length < 2) gesture.current.dist = 0;
	};

	const button: React.CSSProperties = { background: "rgba(255,255,255,0.14)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "999px", minWidth: "38px", height: "38px", padding: "0 14px", fontSize: "15px", fontWeight: 600 };

	return <div style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(10,10,14,0.94)", display: "flex", flexDirection: "column" }} role="dialog" aria-modal="true" aria-label="Foto ampliada">
		<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", color: "#fff", flexWrap: "wrap" }}>
			<div style={{ flex: "1 1 160px", minWidth: 0, fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{alt}</div>
			<button type="button" style={button} aria-label="Alejar" disabled={scale <= MIN} onClick={() => zoomTo((c) => c / 1.5)}>−</button>
			<span style={{ minWidth: "48px", textAlign: "center", fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>{Math.round(scale * 100)}%</span>
			<button type="button" style={button} aria-label="Acercar" disabled={scale >= MAX} onClick={() => zoomTo((c) => c * 1.5)}>+</button>
			<button type="button" style={button} disabled={scale === 1} onClick={() => zoomTo(1)}>Ajustar</button>
			<button type="button" style={{ ...button, background: "#fff", color: "#111" }} onClick={onClose}>Cerrar</button>
		</div>
		<div ref={stage} style={{ flex: 1, overflow: "hidden", position: "relative", touchAction: "none", cursor: scale > 1 ? "grab" : "zoom-in", userSelect: "none" }}
			onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
			onDoubleClick={(event) => { const p = center(event.clientX, event.clientY); zoomTo(scale > 1 ? 1 : 2.5, p.x, p.y); }}>
			<img src={url} alt={alt} draggable={false}
				style={{ position: "absolute", left: "50%", top: "50%", maxWidth: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)", objectFit: "contain",
					transform: `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) scale(${scale})`, transformOrigin: "center center",
					transition: pointers.current.size ? "none" : "transform 0.12s ease-out", background: "#fff", borderRadius: scale === 1 ? "6px" : 0 }} />
		</div>
		<div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: "12px", padding: "6px 0 12px" }}>Rueda del mouse o pellizca para acercar · arrastra para mover · doble clic para alternar</div>
	</div>;
};

export default FichaProducto;
